import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Car,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  MoreHorizontal,
  Package,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  Truck,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Skeleton } from 'components/ui/skeleton';
import './PurchaseOrders.css';
import { getAllPurchaseOrders, getPurchaseOrdersByCategory, createPurchaseOrder } from '../services/purchaseOrdersService';
import { convertToDatabaseFormat } from '../services/purchaseOrdersService';
import { getAllSuppliers } from '../services/suppliersService';
import { useLotScope } from '../hooks/useLotScope';

const PO_LOT_TAB_OPTIONS = [
  { value: 'all', label: 'All LOTs', variant: 'all' },
  { value: 'LOT 8 (Morogoro)', label: 'LOT 8', variant: '8' },
  { value: 'LOT 10 (Manyara)', label: 'LOT 10', variant: '10' },
];

const CATEGORY_DEFINITIONS = [
  { id: 'material', label: 'Material', icon: Package, color: '#1e90ff' },
  { id: 'transportation', label: 'Transportation', icon: Truck, color: '#10b981' },
  { id: 'fleet', label: 'Fleet', icon: Car, color: '#f59e0b' },
  { id: 'others', label: 'Others', icon: MoreHorizontal, color: '#64748b' },
];

function poMatchesLotFilter(po, lotFilter) {
  if (lotFilter === 'all') return true;
  return String(po?.lot || '').trim() === lotFilter;
}

function PoPageSkeleton() {
  return (
    <div className="po-skeleton" aria-busy="true" aria-label="Loading purchase orders">
      <section className="po-intro" aria-hidden>
        <div className="po-intro-text">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="mt-2.5 h-4 w-80 max-w-full" />
        </div>
        <div className="po-intro-actions">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <Skeleton className="h-9 w-[9rem] rounded-lg" />
        </div>
      </section>
      <div className="po-lot-tabs" aria-hidden>
        <Skeleton className="h-9 w-24 rounded-md" />
        <Skeleton className="h-9 w-20 rounded-md" />
        <Skeleton className="h-9 w-20 rounded-md" />
      </div>
      <div className="po-categories-grid" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="po-category-card po-category-card--skel">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="mt-3 h-4 w-24" />
            <Skeleton className="mt-2 h-6 w-36" />
            <Skeleton className="mt-4 h-3 w-full" />
            <Skeleton className="mt-2 h-3 w-[80%]" />
          </div>
        ))}
      </div>
      <div className="po-toolbar" aria-hidden>
        <Skeleton className="h-9 w-full max-w-md rounded-lg" />
        <div className="po-toolbar__spacer" />
        <Skeleton className="h-9 w-28 rounded-lg" />
        <Skeleton className="h-9 w-28 rounded-lg" />
      </div>
      <div className="po-table-section" aria-hidden>
        <div className="po-table-shell">
          <div className="po-skel-table-rows">
            {Array.from({ length: 6 }, (_, r) => (
              <Skeleton key={r} className="h-10 w-full rounded-md" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PurchaseOrders() {
  const [selectedCategory, setSelectedCategory] = useState('material');
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { lotFilter, setLotFilter, lockedLotFilter, isReadOnly } = useLotScope('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [poData, setPoData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    lot: '',
    poNumber: '',
    category: 'transportation',
    subcategory: 'shipping',
    description: '',
    supplierName: ''
  });
  const [items, setItems] = useState([
    { id: 1, itemName: '', qty: '', unitPrice: '' }
  ]);
  const [includeVat, setIncludeVat] = useState(false);

  const subcategories = {
    material: ['Wooden poles', 'Concrete Poles', 'Cables', 'Fittings', 'Meters', 'Transformers'],
    transportation: [
      'Local transportation of Wooden poles',
      'Local transportation of concrete poles',
      'Local transportation of cables',
      'Local transportation of fittings',
      'Local transportation of meters',
      'Local transportation of Transformers'
    ],
    fleet: ['SUV', 'Pick up', 'Crane', 'Fuel'],
    others: ['Stationary', 'warehouse rental', 'PPE', 'Printing', 'Dash camera']
  };

  const [suppliersList, setSuppliersList] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);

  // Filter suppliers by current PO subcategory (supplier.category vs formData.subcategory)
  const suppliersFilteredBySubcategory = useMemo(() => {
    if (!suppliersList.length) return [];
    const sub = (formData.subcategory || '').trim().toLowerCase();
    if (!sub) return suppliersList;
    return suppliersList.filter((s) => {
      const cat = (s.category || '').trim().toLowerCase();
      if (!cat) return false;
      return cat === sub || sub.includes(cat) || cat.includes(sub);
    });
  }, [suppliersList, formData.subcategory]);

  // If no suppliers match subcategory, show all so user can still pick
  const suppliersForSelect = suppliersFilteredBySubcategory.length > 0 ? suppliersFilteredBySubcategory : suppliersList;

  const parseCurrency = (currencyString) => {
    if (!currencyString) return 0;
    const cleaned = currencyString.toString().replace(/TZS\s?/gi, '').replace(/,/g, '').trim();
    return parseFloat(cleaned) || 0;
  };

  const formatCurrency = (amount) => {
    return `TZS ${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const [allPurchaseOrders, setAllPurchaseOrders] = useState([]);

  const refreshAllPurchaseOrders = useCallback(async () => {
    try {
      const allData = await getAllPurchaseOrders();
      setAllPurchaseOrders(allData || []);
    } catch (err) {
      console.error('Error loading purchase orders for category stats:', err);
    }
  }, []);

  // Calculate totals from items - Total Amount = qty * unitPrice
  const calculateItemTotal = (item) => {
    const qty = parseFloat(item.qty) || 0;
    const price = parseFloat(item.unitPrice) || 0;
    return qty * price;
  };

  const poAmountNum = items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  const vatAmount = includeVat ? poAmountNum * 0.18 : 0;
  const totalAmount = poAmountNum + vatAmount;

  // Fetch all suppliers from database (Suppliers table)
  useEffect(() => {
    let cancelled = false;
    setLoadingSuppliers(true);
    getAllSuppliers()
      .then((list) => {
        if (!cancelled) setSuppliersList(list || []);
      })
      .catch((err) => {
        console.error('Error fetching suppliers:', err);
        if (!cancelled) setSuppliersList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSuppliers(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Clear supplier selection when it's no longer in the filtered list (e.g. after subcategory change)
  useEffect(() => {
    if (!formData.supplierName) return;
    const inList = suppliersForSelect.some((s) => (s.name || '').trim() === (formData.supplierName || '').trim());
    if (!inList) {
      setFormData((prev) => ({ ...prev, supplierName: '' }));
    }
  }, [suppliersForSelect, formData.supplierName]);

  // Fetch purchase orders from database with pagination and category filter
  const fetchPurchaseOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await getPurchaseOrdersByCategory(selectedCategory, itemsPerPage, offset);
      setPoData(response.documents || []);
      setTotalDocuments(response.total || 0);
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      const msg = err?.message || '';
      const isNetworkError = msg === 'Failed to fetch' || err?.code === 'ERR_NETWORK';
      const message = isNetworkError
        ? 'Cannot reach the server. Check your internet connection and that the Appwrite endpoint is correct (e.g. REACT_APP_APPWRITE_ENDPOINT in .env).'
        : (msg || 'Please check your connection and try again.');
      setError(`Failed to load purchase orders: ${message}`);
      setPoData([]);
      setTotalDocuments(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, selectedCategory]);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [fetchPurchaseOrders]);

  useEffect(() => {
    refreshAllPurchaseOrders();
  }, [refreshAllPurchaseOrders]);

  // Reset to page 1 when category or LOT filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, lotFilter, searchQuery, statusFilter]);

  const categoryStats = useMemo(() => {
    return CATEGORY_DEFINITIONS.map((cat) => {
      const categoryData = allPurchaseOrders.filter(
        (po) => po.category === cat.id && poMatchesLotFilter(po, lotFilter)
      );

      const totalAmountNum = categoryData.reduce((sum, po) => sum + parseCurrency(po.totalAmount), 0);
      const invoicedAmountNum = categoryData.reduce((sum, po) => sum + parseCurrency(po.invoicedAmount), 0);
      const poBalanceNum = categoryData.reduce((sum, po) => sum + parseCurrency(po.poBalance), 0);
      const invoicedPercent = totalAmountNum > 0
        ? ((invoicedAmountNum / totalAmountNum) * 100).toFixed(1)
        : '0.0';
      const poBalancePercent = totalAmountNum > 0
        ? ((poBalanceNum / totalAmountNum) * 100).toFixed(1)
        : '0.0';

      return {
        ...cat,
        poCount: categoryData.length,
        totalAmount: formatCurrency(totalAmountNum),
        invoicedPercent: `${invoicedPercent}%`,
        invoicedAmount: formatCurrency(invoicedAmountNum),
        poBalancePercent: `${poBalancePercent}%`,
        poBalance: formatCurrency(poBalanceNum)
      };
    });
  }, [allPurchaseOrders, lotFilter]);

  // Calculate selected category data from categoryStats
  const selectedCategoryData = categoryStats.find(cat => cat.id === selectedCategory);
  const CategoryIcon = selectedCategoryData?.icon || Package;

  const hasActiveTableFilters =
    searchQuery.trim() !== '' || lotFilter !== 'all' || statusFilter !== 'all';

  // Filter purchase orders based on search and filters.
  // When any table filter/search is active, search the full category list
  // (allPurchaseOrders) — not just the current paginated page of 10.
  const filteredPoData = useMemo(() => {
    const source = hasActiveTableFilters
      ? (allPurchaseOrders || []).filter((po) => po.category === selectedCategory)
      : (poData || []);

    if (!source.length) {
      return [];
    }

    return source.filter((po) => {
      // Filter by LOT
      if (!poMatchesLotFilter(po, lotFilter)) {
        return false;
      }

      // Filter by status
      if (
        statusFilter !== 'all' &&
        po.status &&
        String(po.status).toLowerCase() !== statusFilter
      ) {
        return false;
      }

      // Filter by search query
      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase().trim();
      const searchableText = [
        po.lot || '',
        po.poNumber || '',
        po.poDate || '',
        po.subcategory || '',
        po.description || '',
        po.supplierName || '',
        po.poAmount || '',
        po.vat || '',
        po.totalAmount || '',
        po.invoicedAmount || '',
        po.poBalance || '',
        po.status || ''
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [
    poData,
    allPurchaseOrders,
    selectedCategory,
    hasActiveTableFilters,
    lotFilter,
    statusFilter,
    searchQuery
  ]);

  // Calculate pagination based on filtered data
  const totalPages = Math.ceil(totalDocuments / itemsPerPage);
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, totalDocuments);
  const indexOfFirstItem = totalDocuments > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0;

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
    // Scroll to top of table
    const tableWrapper = document.querySelector('.po-details-table-wrapper');
    if (tableWrapper) {
      tableWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  // Calculate subtotals from filtered data (for display)
  const calculateSubtotals = () => {
    const totals = filteredPoData.reduce((acc, po) => {
      acc.poAmount += parseCurrency(po.poAmount);
      acc.vat += parseCurrency(po.vat);
      acc.totalAmount += parseCurrency(po.totalAmount);
      acc.invoicedAmount += parseCurrency(po.invoicedAmount);
      acc.poBalance += parseCurrency(po.poBalance);
      return acc;
    }, {
      poAmount: 0,
      vat: 0,
      totalAmount: 0,
      invoicedAmount: 0,
      poBalance: 0
    });
    return totals;
  };

  const subtotals = calculateSubtotals();

  const addItem = () => {
    const newId = items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 1;
    setItems([...items, { id: newId, itemName: '', qty: '', unitPrice: '' }]);
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setLotFilter('all');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  // Export to Excel - exports ALL data from database, ignoring ALL filters
  const handleExportToExcel = async () => {
    try {
      // Fetch ALL purchase orders from database (ignores category, pagination, and all filters)
      const allRows = await getAllPurchaseOrders();
      
      if (!allRows || allRows.length === 0) {
        alert('No data to export');
        return;
      }

      // Prepare data for export - use ALL data from database, ignoring all filters
      const exportData = allRows.map(po => ({
        'LOT Name': po.lot || '',
        'PO Number': po.poNumber || '',
        'PO Date': po.poDate || '',
        'Category': po.category || '',
        'Subcategory': po.subcategory || '',
        'Description': po.description || '',
        'Supplier Name': po.supplierName || '',
        'PO Amount': po.poAmount || '',
        'VAT 18%': po.vat || '',
        'Total Amount': po.totalAmount || '',
        'Invoiced Amount': po.invoicedAmount || '',
        'PO Balance': po.poBalance || '',
        'Status': po.status || ''
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Purchase Orders');

      // Set column widths
      const colWidths = [
        { wch: 20 }, // LOT Name
        { wch: 20 }, // PO Number
        { wch: 15 }, // PO Date
        { wch: 15 }, // Category
        { wch: 30 }, // Subcategory
        { wch: 40 }, // Description
        { wch: 20 }, // Supplier Name
        { wch: 18 }, // PO Amount
        { wch: 15 }, // VAT 18%
        { wch: 18 }, // Total Amount
        { wch: 18 }, // Invoiced Amount
        { wch: 18 }, // PO Balance
        { wch: 15 }  // Status
      ];
      ws['!cols'] = colWidths;

      // Generate Excel file and download
      const fileName = `Purchase_Orders_All_Data_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      alert(`Successfully exported ${exportData.length} purchase orders to ${fileName}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert(`Failed to export data: ${error.message || 'Please try again.'}`);
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    setFormData(prev => ({
      ...prev,
      category: newCategory,
      subcategory: subcategories[newCategory]?.[0] || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    
    try {
      // Get current date in dd/mm/yyyy format
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      const currentDate = `${day}/${month}/${year}`;

      // Prepare PO data
      const poDataToSave = {
        lot: formData.lot,
        poNumber: formData.poNumber.trim(),
        poDate: currentDate,
        category: formData.category,
        subcategory: formData.subcategory,
        description: formData.description,
        supplierName: formData.supplierName,
        items: items.filter(item => item.itemName && item.qty && item.unitPrice),
        poAmount: poAmountNum,
        vat: vatAmount,
        totalAmount: totalAmount,
        invoicedAmount: 0,
        poBalance: totalAmount,
        status: 'pending'
      };

      // Convert to database format and save
      const dbData = convertToDatabaseFormat(poDataToSave);
      await createPurchaseOrder(dbData);

      // Refresh the data - reset to page 1 and fetch (no category filter)
      setCurrentPage(1);
      const offset = 0;
      const response = await getPurchaseOrdersByCategory(null, itemsPerPage, offset);
      setPoData(response.documents);
      setTotalDocuments(response.total);
      
      // Update category stats
      await refreshAllPurchaseOrders();

      // Close modal and reset form
      setShowModal(false);
      setFormData({
        lot: '',
        poNumber: '',
        category: 'transportation',
        subcategory: 'shipping',
        description: '',
        supplierName: ''
      });
      setItems([{ id: 1, itemName: '', qty: '', unitPrice: '' }]);
      setIncludeVat(false);
    } catch (err) {
      console.error('Error creating purchase order:', err);
      setError(err?.message || 'Failed to create purchase order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (saving) return;
    setShowModal(false);
    setError(null);
    setFormData({
      lot: '',
      poNumber: '',
      category: 'transportation',
      subcategory: 'shipping',
      description: '',
      supplierName: ''
    });
    setItems([{ id: 1, itemName: '', qty: '', unitPrice: '' }]);
    setIncludeVat(false);
  };

  return (
    <div className="po-page">
      <div className="po-scroll">
        <div className="po-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Logistics', to: '/sagemcom/logistics', hideOnMobile: true },
              { label: 'Purchase orders' },
            ]}
          />

          {loading && !allPurchaseOrders.length ? (
            <PoPageSkeleton />
          ) : (
            <>
              <section className="po-intro" aria-label="Purchase orders overview">
                <div className="po-intro-text">
                  <h1 className="po-intro-title">Purchase orders</h1>
                  <p className="po-intro-subtitle">
                    Track PO amounts, invoicing, and balances across Material, Transportation, Fleet, and Others
                  </p>
                </div>
                <div className="po-intro-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="po-soft-btn h-9 w-9"
                    onClick={handleExportToExcel}
                    disabled={loading || allPurchaseOrders.length === 0}
                    title="Download Excel"
                    aria-label="Download Excel"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {!isReadOnly ? (
                  <Button
                    type="button"
                    className="po-create-btn"
                    onClick={() => {
                      setError(null);
                      setShowModal(true);
                    }}
                    disabled={loading}
                  >
                    <Plus className="h-4 w-4" />
                    Record PO
                  </Button>
                  ) : null}
                </div>
              </section>

              {error && !showModal ? (
                <div className="po-error" role="alert">
                  <span>{error}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="po-soft-btn"
                    onClick={fetchPurchaseOrders}
                    disabled={loading}
                  >
                    {loading ? 'Retrying…' : 'Retry'}
                  </Button>
                </div>
              ) : null}

              <div className="po-lot-tabs" role="tablist" aria-label="Filter by LOT">
                {PO_LOT_TAB_OPTIONS.filter(
                  (option) => !lockedLotFilter || option.value === lockedLotFilter
                ).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="tab"
                    aria-selected={lotFilter === option.value}
                    className={`po-lot-tabs__btn${lotFilter === option.value ? ' is-active' : ''}`}
                    onClick={() => {
                      if (!lockedLotFilter) setLotFilter(option.value);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="po-categories-grid">
                {categoryStats.map((category) => {
                  const IconComponent = category.icon;
                  const isSelected = selectedCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      type="button"
                      className={`po-category-card${isSelected ? ' is-selected' : ''}`}
                      onClick={() => setSelectedCategory(category.id)}
                      style={{ '--cat-accent': category.color }}
                      aria-pressed={isSelected}
                    >
                      <div className="po-category-card__top">
                        <span className="po-category-card__icon" aria-hidden>
                          <IconComponent className="h-4 w-4" />
                        </span>
                        <span className="po-category-card__label">{category.label}</span>
                        {isSelected ? (
                          <span className="po-category-card__check" aria-hidden>
                            <Check className="h-3.5 w-3.5" />
                          </span>
                        ) : null}
                      </div>
                      <div className="po-category-card__count">{category.poCount} POs</div>
                      <div className="po-category-card__amount">{category.totalAmount}</div>
                      <div className="po-category-card__meta">
                        <div>
                          <span>Invoiced {category.invoicedPercent}</span>
                          <strong>{category.invoicedAmount}</strong>
                        </div>
                        <div>
                          <span>Balance {category.poBalancePercent}</span>
                          <strong>{category.poBalance}</strong>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="po-table-section__head">
                <div className="po-table-section__title-row">
                  <CategoryIcon className="h-4 w-4" style={{ color: selectedCategoryData?.color }} aria-hidden />
                  <h2>{selectedCategoryData?.label || 'Category'} details</h2>
                </div>
              </div>

              <div className="po-toolbar">
                <div className="po-search">
                  <Search className="po-search__icon h-4 w-4" aria-hidden />
                  <Input
                    className="po-search__input"
                    placeholder="Search LOT, PO #, supplier, description…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search purchase orders"
                  />
                </div>
                <div className="po-toolbar__spacer" aria-hidden="true" />
                <div className="po-filters">
                  <select
                    className="po-filter-select"
                    value={lotFilter}
                    onChange={(e) => setLotFilter(e.target.value)}
                    aria-label="LOT filter"
                  >
                    <option value="all">All LOTs</option>
                    <option value="LOT 8 (Morogoro)">LOT 8 (Morogoro)</option>
                    <option value="LOT 10 (Manyara)">LOT 10 (Manyara)</option>
                  </select>
                  <select
                    className="po-filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Status filter"
                  >
                    <option value="all">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="draft">Draft</option>
                    <option value="overdue">Overdue</option>
                  </select>
                  {(searchQuery || lotFilter !== 'all' || statusFilter !== 'all') ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="po-soft-btn"
                      onClick={clearAllFilters}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Clear
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="po-table-section">
                <div className="po-table-shell">
                  <div className="po-details-table-wrapper">
                    <table className="po-details-table">
                      {!loading && filteredPoData.length > 0 ? (
                        <thead className="subtotal-header">
                          <tr className="subtotal-row">
                            <th colSpan={6}>
                              Subtotal ({filteredPoData.length}{' '}
                              {filteredPoData.length === 1 ? 'item' : 'items'})
                            </th>
                            <th>{formatCurrency(subtotals.poAmount)}</th>
                            <th>{formatCurrency(subtotals.vat)}</th>
                            <th>{formatCurrency(subtotals.totalAmount)}</th>
                            <th>{formatCurrency(subtotals.invoicedAmount)}</th>
                            <th>{formatCurrency(subtotals.poBalance)}</th>
                          </tr>
                        </thead>
                      ) : null}
                      <thead>
                        <tr>
                          <th className="po-col-lot">LOT</th>
                          <th>PO Number</th>
                          <th>PO Date</th>
                          <th>Subcategory</th>
                          <th>Description</th>
                          <th className="po-col-supplier">Supplier</th>
                          <th>PO Amount</th>
                          <th>VAT 18%</th>
                          <th>Total</th>
                          <th>Invoiced</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          Array.from({ length: 6 }, (_, r) => (
                            <tr key={`skel-${r}`} className="po-table-skel-row">
                              {Array.from({ length: 11 }, (_, c) => (
                                <td key={c}>
                                  <Skeleton className="h-4 w-[85%]" />
                                </td>
                              ))}
                            </tr>
                          ))
                        ) : filteredPoData.length > 0 ? (
                          filteredPoData.map((po) => (
                            <tr key={po.id}>
                              <td className="po-col-lot">{po.lot || '—'}</td>
                              <td className="po-cell-strong">{po.poNumber || '—'}</td>
                              <td>{po.poDate || '—'}</td>
                              <td>
                                <span
                                  className="po-subcategory-badge"
                                  style={{
                                    backgroundColor: `${selectedCategoryData?.color}18`,
                                    color: selectedCategoryData?.color,
                                  }}
                                >
                                  {po.subcategory || '—'}
                                </span>
                              </td>
                              <td
                                className="po-cell-desc"
                                title={po.description ? String(po.description).trim() : undefined}
                              >
                                {po.description || '—'}
                              </td>
                              <td className="po-col-supplier">{po.supplierName || '—'}</td>
                              <td className="po-cell-num">{po.poAmount || '—'}</td>
                              <td className="po-cell-num">{po.vat || '—'}</td>
                              <td className="po-cell-num">{po.totalAmount || '—'}</td>
                              <td className="po-cell-num">{po.invoicedAmount || '—'}</td>
                              <td className="po-cell-num">{po.poBalance || '—'}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={11} className="po-table-empty">
                              {poData.length > 0
                                ? 'No purchase orders match your filters.'
                                : 'No purchase orders yet. Record a PO to get started.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {totalDocuments > 0 && !hasActiveTableFilters ? (
                  <div className="po-pagination">
                    <div className="po-pagination__info">
                      Showing {indexOfFirstItem}–{indexOfLastItem} of {totalDocuments}
                    </div>
                    <div className="po-pagination__controls">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="po-soft-btn h-8 w-8"
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="po-pagination__numbers">
                        {getPageNumbers().map((pageNum, index) =>
                          pageNum === '...' ? (
                            <span key={`ellipsis-${index}`} className="po-pagination__ellipsis">
                              …
                            </span>
                          ) : (
                            <button
                              key={pageNum}
                              type="button"
                              className={`po-pagination__num${currentPage === pageNum ? ' is-active' : ''}`}
                              onClick={() => goToPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          )
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="po-soft-btn h-8 w-8"
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : null}

                {hasActiveTableFilters ? (
                  <div className="po-filtered-count">
                    Showing {filteredPoData.length} matching purchase order
                    {filteredPoData.length === 1 ? '' : 's'} in{' '}
                    {selectedCategoryData?.label || 'this category'}
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {showModal ? (
        <div className="po-modal-overlay" onClick={handleCancel}>
          <div
            className="po-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="po-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="po-modal__header">
              <h2 id="po-modal-title">Record purchase order</h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="po-soft-btn"
                onClick={handleCancel}
                disabled={saving}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form className="po-modal__form" onSubmit={handleSubmit}>
              {error ? (
                <div className="po-modal__error" role="alert">
                  {error}
                </div>
              ) : null}

              <div className="po-field-row">
                <div className="po-field">
                  <Label htmlFor="lot">LOT</Label>
                  <select
                    id="lot"
                    name="lot"
                    className="po-select"
                    value={formData.lot}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select LOT</option>
                    <option value="LOT 8 (Morogoro)">LOT 8 (Morogoro)</option>
                    <option value="LOT 10 (Manyara)">LOT 10 (Manyara)</option>
                  </select>
                </div>
                <div className="po-field">
                  <Label htmlFor="poNumber">PO Number</Label>
                  <Input
                    id="poNumber"
                    name="poNumber"
                    value={formData.poNumber}
                    onChange={handleInputChange}
                    placeholder="Enter PO number"
                    required
                  />
                  <p className="po-field-hint">Must be unique across the system.</p>
                </div>
              </div>

              <div className="po-field-row">
                <div className="po-field">
                  <Label htmlFor="category">Category</Label>
                  <select
                    id="category"
                    name="category"
                    className="po-select"
                    value={formData.category}
                    onChange={handleCategoryChange}
                  >
                    {CATEGORY_DEFINITIONS.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="po-field">
                  <Label htmlFor="subcategory">Subcategory</Label>
                  <select
                    id="subcategory"
                    name="subcategory"
                    className="po-select"
                    value={formData.subcategory}
                    onChange={handleInputChange}
                  >
                    {subcategories[formData.category]?.map((sub) => (
                      <option key={sub} value={sub.toLowerCase()}>
                        {sub}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="po-field">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Optional description"
                />
              </div>

              <div className="po-field">
                <Label htmlFor="supplierName">
                  Supplier
                  {suppliersFilteredBySubcategory.length > 0 &&
                  suppliersFilteredBySubcategory.length < suppliersList.length ? (
                    <span className="po-field-hint-inline"> · filtered by subcategory</span>
                  ) : null}
                </Label>
                <select
                  id="supplierName"
                  name="supplierName"
                  className="po-select"
                  value={formData.supplierName}
                  onChange={handleInputChange}
                  disabled={loadingSuppliers}
                >
                  <option value="">
                    {loadingSuppliers ? 'Loading suppliers…' : 'Select supplier'}
                  </option>
                  {suppliersForSelect.map((supplier) => (
                    <option key={supplier.id} value={supplier.name || ''}>
                      {supplier.name || ''}
                      {supplier.category ? ` — ${supplier.category}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="po-items">
                <div className="po-items__head">
                  <Label>Items</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="po-soft-btn"
                    onClick={addItem}
                    disabled={saving}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add item
                  </Button>
                </div>
                <div className="po-items__list">
                  <div className="po-items__cols" aria-hidden>
                    <span>#</span>
                    <span>Item</span>
                    <span>Qty</span>
                    <span>Unit price</span>
                    <span>Total</span>
                    <span />
                  </div>
                  {items.map((item, index) => (
                    <div key={item.id} className="po-items__row">
                      <span className="po-items__num">{index + 1}</span>
                      <Input
                        type="text"
                        placeholder="Item name"
                        value={item.itemName}
                        onChange={(e) => updateItem(item.id, 'itemName', e.target.value)}
                        disabled={saving}
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.qty}
                        onChange={(e) => updateItem(item.id, 'qty', e.target.value)}
                        min="0"
                        step="0.001"
                        disabled={saving}
                      />
                      <Input
                        type="number"
                        placeholder="Price"
                        value={item.unitPrice}
                        onChange={(e) => updateItem(item.id, 'unitPrice', e.target.value)}
                        min="0"
                        step="0.01"
                        disabled={saving}
                      />
                      <span className="po-items__total">{formatCurrency(calculateItemTotal(item))}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="po-soft-btn h-8 w-8"
                        onClick={() => removeItem(item.id)}
                        disabled={items.length === 1 || saving}
                        aria-label="Remove item"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="po-totals">
                <label className="po-vat-toggle">
                  <input
                    type="checkbox"
                    checked={includeVat}
                    onChange={(e) => setIncludeVat(e.target.checked)}
                    disabled={saving}
                  />
                  <span>Include VAT (18%)</span>
                </label>
                <div className="po-totals__rows">
                  <div>
                    <span>PO amount</span>
                    <strong>{formatCurrency(poAmountNum)}</strong>
                  </div>
                  <div>
                    <span>VAT (18%)</span>
                    <strong>{formatCurrency(vatAmount)}</strong>
                  </div>
                  <div className="po-totals__grand">
                    <span>Total</span>
                    <strong>{formatCurrency(totalAmount)}</strong>
                  </div>
                </div>
              </div>

              <div className="po-modal__actions">
                <Button
                  type="button"
                  variant="ghost"
                  className="po-soft-btn"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Record PO
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PurchaseOrders;
